import { V12FianceProduct } from 'shared/types/payment/V12PaymentOption';
import { DateHelpers } from 'helpers/dateHelpers';

// Calculates all financial parameters
export const calculate = (selectedFinance: V12FianceProduct, cashPrice: number, deposit: number) => {
  if (selectedFinance === undefined) {
    return;
  }

  const apr = selectedFinance.APR;
  const monthlyrate = selectedFinance.MonthlyRate;
  let calculatedApr;
  const months = selectedFinance.Months;
  const serviceFee = selectedFinance.ServiceFee;
  let balancePayable = 0.0;
  let documentFee = 0;
  let initialPayments;
  let finalPayment;
  let profit;
  let pv;
  let productAvailable;
  let availabilityReason;
  const loanAmount = cashPrice - deposit;

  documentFee = selectedFinance.DocumentFee + loanAmount * selectedFinance.DocumentFeePercentage;

  if (selectedFinance.DocumentFeeMinimum > 0 && documentFee < selectedFinance.DocumentFeeMinimum) {
    documentFee = selectedFinance.DocumentFeeMinimum;
  }
  if (selectedFinance.DocumentFeeMaximum > 0 && documentFee > selectedFinance.DocumentFeeMaximum) {
    documentFee = selectedFinance.DocumentFeeMaximum;
  }

  if (monthlyrate === 0) {
    initialPayments = Math.round((loanAmount / months) * 100) / 100;

    if (initialPayments * months < loanAmount) {
      initialPayments += 0.01;
    }

    finalPayment = loanAmount - initialPayments * (months - 1);

    balancePayable = loanAmount;

    calculatedApr = 0;

    if (documentFee > 0) {
      calculatedApr = calculateAprFromIrr(
        loanAmount,
        initialPayments,
        months,
        documentFee,
        selectedFinance.DocumentFeeCollectionMonth,
      );
    }
  } else {
    profit = (apr / 100 + 1) ** (1.0 / 12);

    pv = loanAmount - serviceFee;

    if (selectedFinance.DeferredPeriod > 1) {
      pv *= profit ** (selectedFinance.DeferredPeriod - 1);
    }

    initialPayments = Math.floor((-pv / ((profit ** -months - 1) / (profit - 1))) * 100) / 100;

    finalPayment = initialPayments;

    balancePayable = initialPayments * months;

    calculatedApr = calculateApr(
      loanAmount - selectedFinance.ServiceFee,
      initialPayments,
      selectedFinance.DeferredPeriod,
      months,
    );

    if (documentFee > 0) {
      calculatedApr = calculateAprFromIrr(
        loanAmount,
        initialPayments,
        months,
        documentFee,
        selectedFinance.DocumentFeeCollectionMonth,
      );
    }
  }

  // var x = parent.calculateAprFromIrr(loanAmount, initialPayments, months,
  //     parseFloat(financeProduct.documentFee), parseFloat(financeProduct.documentFeeCollectionMonth));

  balancePayable = initialPayments * (months - 1);
  balancePayable += finalPayment;

  const interest = balancePayable - loanAmount;
  const chargeForCredit = interest + serviceFee + documentFee;
  const amountPayable = loanAmount + chargeForCredit;

  productAvailable = true;
  availabilityReason = '';

  if (loanAmount < selectedFinance.MinLoan) {
    productAvailable = false;
    availabilityReason = `Only available on loan amounts over £${selectedFinance.MinLoan.toFixed(2)}`;
  } else if (loanAmount > selectedFinance.MaxLoan) {
    productAvailable = false;
    availabilityReason = `Only available on loan amounts under £${selectedFinance.MaxLoan.toFixed(2)}`;
  }

  const financeCalculation = {
    initialPayments: initialPayments.toFixed(2),
    finalPayment: finalPayment.toFixed(2),
    balancePayable: balancePayable.toFixed(2),
    interest: interest.toFixed(2),
    chargeForCredit: chargeForCredit.toFixed(2),
    amountPayable: amountPayable.toFixed(2),
    cashPrice: cashPrice.toFixed(2),
    deposit: deposit.toFixed(2),
    loanAmount: loanAmount.toFixed(2),
    months,
    monthsDeferred: selectedFinance.DeferredPeriod,
    apr: calculatedApr.toFixed(2),
    productAvailable,
    availabilityReason,
    productId: selectedFinance.ProductId,
    productGuid: selectedFinance.ProductGuid,
    name: selectedFinance.Name,
    settlementFee: selectedFinance.SettlementFee.toFixed(2),
    serviceFee: serviceFee.toFixed(2),
    documentFee: documentFee.toFixed(2),
    DocumentFeeMinimum: selectedFinance.DocumentFeeMinimum,
    documentFeeMaximum: selectedFinance.DocumentFeeMaximum,
    documentFeeCollectionMonth: selectedFinance.DocumentFeeCollectionMonth,
    documentFeePercentage: selectedFinance.DocumentFeePercentage,
  };

  return financeCalculation;
};

const calculateApr = (loan: number, instalment: number, deferred: number, term: number) => {
  let result = parseFloat('0');
  let high = parseFloat('200');
  let low = parseFloat('0');
  let n;
  let x;
  let j;
  let q;
  let y;
  let z;

  if (deferred > 1) {
    n = term + deferred + 1;
  } else {
    n = term + 1;
  }

  x = 1;

  while (x < 20) {
    result = (high + low) / 2;

    j = (1.0 + result / 100.0) ** (1.0 / 12.0);

    q = 1.0 / j;

    if (deferred < 1) {
      y = instalment * ((1.0 - q ** n) / (1 - q)) - instalment;

      z = 0.0;
    } else {
      y = (instalment * (1.0 - q ** (n - 1))) / (1 - q) - instalment;

      z = (instalment * (1.0 - q ** deferred)) / (1 - q) - instalment;
    }

    if (y - z < loan) {
      high = result;
    } else {
      low = result;
    }

    x++;
  }

  return result;
};

const calculateAprFromIrr = (
  loan: number,
  monthlyinstalment: number,
  loanTerm: number,
  documentfee: number,
  documentfeecollectionmonth: number,
) => {
  const startDate = new Date();
  const incomeTable = [];
  const dateTable: Date[] = [];
  let i;
  let nextDate;

  if (documentfeecollectionmonth === 0) {
    incomeTable.push(loan * -1 + documentfee);
  } else {
    incomeTable.push(loan * -1);
  }
  dateTable.push(startDate);

  for (i = 1; i <= loanTerm; i++) {
    nextDate = DateHelpers.addMonths(startDate, i);
    dateTable.push(nextDate);

    if (i - 1 === documentfeecollectionmonth && documentfeecollectionmonth > 0) {
      incomeTable.push(monthlyinstalment + documentfee);
    } else {
      incomeTable.push(monthlyinstalment);
    }
  }
  /* eslint-disable new-cap */
  const r = XIRR(incomeTable, dateTable, 0.1);
  /* eslint-enable new-cap */

  return Math.round(r * 10000) / 100;
};

const XIRR = (values: number[], dates: Date[], guess: number) => {
  // Credits: algorithm inspired by Apache OpenOfficedateHelpers.ts
  // Calculates the resulting amount
  const irrResult = function (pValues: number[], pDates: Date[], rate: number) {
    const r = rate + 1;
    let result = pValues[0];
    let i;
    for (i = 1; i < pValues.length; i++) {
      result += pValues[i] / r ** (DateHelpers.daysBetween(pDates[i], pDates[0]) / 365);
    }
    return result;
  };

  // Calculates the first derivation
  const irrResultDeriv = function (pValues: number[], pDates: Date[], rate: number) {
    const r = rate + 1;
    let result = 0;
    let i;
    let frac;
    for (i = 1; i < pValues.length; i++) {
      frac = DateHelpers.daysBetween(pDates[i], pDates[0]) / 365;
      result -= frac * (pValues[i] / r ** (frac + 1));
    }
    return result;
  };

  // Check that values contains at least one positive value and one negative value
  let positive = false;
  let negative = false;
  let i;
  let resultRate;
  let newRate;
  let epsRate;
  let resultValue;
  let iteration;
  let contLoop;
  for (i = 0; i < values.length; i++) {
    if (values[i] > 0) {
      positive = true;
    }
    if (values[i] < 0) {
      negative = true;
    }
  }

  // Return error if values does not contain at least one positive value and one negative value
  if (!positive || !negative) {
    throw new Error('calcualtion error');
  }

  // Initialize guess and resultRate
  const pGuess = guess === undefined ? 0.1 : guess;
  resultRate = pGuess;

  // Set maximum epsilon for end of iteration
  const epsMax = 1e-10;

  // Set maximum number of iterations
  const iterMax = 50;

  // Implement Newton's method
  iteration = 0;
  contLoop = true;
  do {
    resultValue = irrResult(values, dates, resultRate);
    newRate = resultRate - resultValue / irrResultDeriv(values, dates, resultRate);
    epsRate = Math.abs(newRate - resultRate);
    resultRate = newRate;
    contLoop = epsRate > epsMax && Math.abs(resultValue) > epsMax;
  } while (contLoop && ++iteration < iterMax);

  if (contLoop) {
    throw new Error('calcualtion error');
  }

  // Return internal rate of return
  return resultRate;
};
